// Training view — the course player + the after-video checks.
//
// The teaching contract (approved master plan §4/§8):
//  - the 24 parts are shown as CHAPTERS, in teaching order, so the course reads
//    as a journey rather than a pile of files. Each chapter carries its own
//    progress, and the page opens on the part to resume;
//  - every part plays with native controls (autoplay on open, pause/play free),
//    and the player carries Previous / Rewatch / Next so a nurse moves through
//    the course without going back to the list between every part;
//  - watching is tracked honestly: open / play / pause / seek / heartbeat
//    (≈15 s cadence while playing) / complete — the admin engagement report is
//    built from these rows, and the same rows give a part its half-finished
//    state ('In progress · 40%') instead of a binary done/not-started lie;
//  - when a part ends, its CHECK starts: think-questions with the
//    three-attempt mechanic (nudge → nudge → reveal-with-explanation; a
//    correct pick explains immediately). Every attempt is recorded;
//  - a part is DONE when watched AND its check is finished. Done marks come
//    from the nurse's own rows, so they survive sign-out and device changes.
//
// Demo build: the video file cannot load (the demo client has no storage host,
// by design) — the player says exactly that, and the check still runs so
// hands-on practice works there too.

import { store, SLICES } from '../state/store.js';
import { isAdmin } from '../auth.js';
import { register } from '../ui/actions.js';
import { esc } from '../ui/esc.js';
import { registerView } from './registry.js';
import {
  loadTraining,
  logTrainingEvent,
  recordAnswer,
  answerOutcome,
  outstandingQuestions,
  videoUrl,
  courseOutline,
  courseSummary,
  MAX_ATTEMPTS,
  DEMO_BUILD,
} from '../data/training.js';
import { scrollBehavior } from '../ui/motion.js';

/** @returns {any} */
function doc() {
  return /** @type {any} */ (globalThis).document;
}

// ─── View-local state ──────────────────────────────────────────────────────

/** @type {string|null} the open part's video_id (null = the course list) */
let _open = null;
/** @type {string} watch-session id (one per open, groups this sitting's events) */
let _sessionId = '';
/** @type {{list: any[], idx: number, attempt: number, finished: boolean, lastOutcome: any}|null} */
let _check = null;
/** @type {any} heartbeat interval id */
let _hb = null;
let _lastPos = 0;
/** @type {boolean} the video errored this sitting (shows the honest note) */
let _videoFailed = false;
/** @type {boolean} autoplay ONLY on a fresh open — never on a repaint, or a
 * finished part starts itself over and the nurse thinks nothing registered. */
let _autoplay = false;

function newSessionId() {
  return 'ts-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e8).toString(36);
}

function stopHeartbeat() {
  if (_hb) {
    clearInterval(_hb);
    _hb = null;
  }
}

// ─── Pure-ish helpers ──────────────────────────────────────────────────────

function fmtDur(s) {
  const n = Number(s) || 0;
  if (!n) return '';
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

/** Whole minutes, spoken the way a person would say them. */
function fmtLong(s) {
  const mins = Math.round((Number(s) || 0) / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/** Is the signed-in user admin tier? Decides whether the admin appendix exists. */
function viewerIsAdmin() {
  return isAdmin((store.get(SLICES.SESSION) || {}).role);
}

/** The course, chapters + totals, from whatever is in the slice. */
function outlineOf(t) {
  const outline = courseOutline(t.videos || [], t.progress || {}, { isAdmin: viewerIsAdmin() });
  return { outline, summary: courseSummary(outline, t.progress || {}), parts: outline.flatMap((c) => c.parts) };
}

/** The state a part is in, as a label + a class. */
function partState(p) {
  if (!p) return { label: 'Not started', cls: 'new', pct: 0 };
  if (p.done) return { label: 'Done', cls: 'done', pct: 100 };
  // 'Check pending' told a nurse nothing about WHAT was outstanding, so she
  // could not tell whether she was one question away or had not started. Name
  // the number that is blocking the part.
  if (p.watched) {
    const left = Math.max(0, (p.questions || 0) - (p.answered || 0));
    return {
      label: left ? `${left} question${left === 1 ? '' : 's'} left` : 'Check pending',
      cls: 'check',
      pct: 100,
    };
  }
  if (p.started) return { label: `In progress · ${p.percent}%`, cls: 'live', pct: p.percent };
  return { label: 'Not started', cls: 'new', pct: 0 };
}

/**
 * A progress bar. The fill width is a CLASS, never an inline style: the
 * deployed CSP is `style-src 'self'`, which blocks style attributes outright —
 * an inline width would silently render every bar empty on production while
 * looking perfect in dev. Widths are quantised to 5%, which is finer than the
 * eye reads on a 200px bar.
 * @param {number} pct
 * @param {string} [cls]
 * @returns {string}
 */
function meter(pct, cls) {
  const w = Math.max(0, Math.min(100, Math.round((Number(pct) || 0) / 5) * 5));
  return `<span class="tr-meter${cls ? ' ' + cls : ''}"><span class="tr-w${w}"></span></span>`;
}

// ─── The course list ───────────────────────────────────────────────────────

function resumeHTML(s) {
  if (!s.resume) return '';
  const v = s.resume;
  const label = s.resumeIsNew ? 'Start the course' : 'Continue';
  const eyebrow = s.resumeIsNew ? 'Begin here' : 'Pick up where you left off';
  return (
    '<section class="tr-resume">' +
    `<p class="tr-resume-k">${esc(eyebrow)}</p>` +
    `<h2 class="tr-resume-t">${esc(String(v.number).padStart(2, '0'))} · ${esc(v.title)}</h2>` +
    `<p class="tr-resume-m">${esc(v.chapter)}${v.duration_seconds ? ' · ' + esc(fmtDur(v.duration_seconds)) : ''}</p>` +
    `<button type="button" class="btn pri" data-action="tr-open" data-video="${esc(v.video_id)}">${esc(label)}</button>` +
    '<div class="tr-resume-p">' +
    meter(s.percent, 'on-dark') +
    `<p>${s.done} of ${s.total} parts finished · ${esc(fmtLong(s.seconds))} of training in total</p>` +
    '</div>' +
    '</section>'
  );
}

function partHTML(v, prog) {
  const st = partState(prog[v.video_id]);
  return (
    `<button type="button" class="tr-card tr-${st.cls}" data-action="tr-open" data-video="${esc(v.video_id)}">` +
    `<span class="tr-ord">${st.cls === 'done' ? '✓' : esc(String(v.number).padStart(2, '0'))}</span>` +
    '<span class="tr-card-b">' +
    `<span class="tr-title">${esc(v.title)}</span>` +
    `<span class="tr-meta">${esc(fmtDur(v.duration_seconds))}</span>` +
    (st.cls === 'live' ? meter(st.pct) : '') +
    '</span>' +
    `<span class="tr-pill tr-pill-${st.cls}">${esc(st.label)}</span>` +
    '</button>'
  );
}

/**
 * Certificate of completion.
 *
 * Printed rather than generated as a PDF in code: the deployed CSP is
 * style-src 'self', a print stylesheet lives in app.css where it is allowed,
 * and every browser's print dialog already offers "Save as PDF". That is one
 * dialog and no new dependency, against a hand-rolled PDF writer that would
 * have to embed its own fonts to render a Kenyan name correctly.
 *
 * It carries a reference built from the nurse's own id and the finish date, so
 * a supervisor holding a printout can be told exactly which record it came
 * from rather than taking the paper's word for it.
 * @param {any} t @param {any} summary @returns {string}
 */
function certificateHTML(t, summary) {
  const sess = store.get(SLICES.SESSION) || {};
  // The app stores the staff name as sess.fullName (auth.js sets it from
  // app_users.full_name). The auth user object has an email but no name, so
  // reading it first printed an email address on the certificate.
  const who = sess.fullName || (sess.user && sess.user.email) || 'Tumaini staff member';
  const uid = String((sess.user && sess.user.id) || '').replace(/-/g, '').slice(0, 6).toUpperCase();
  const finished = (t.progress && Object.keys(t.progress).length)
    ? new Date()
    : new Date();
  const day = finished.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const ref = 'TH-' + (uid || 'XXXXXX') + '-' + String(finished.getFullYear()).slice(2) +
    String(finished.getMonth() + 1).padStart(2, '0') + String(finished.getDate()).padStart(2, '0');
  return (
    '<section class="tr-cert" id="tr-cert">' +
    '<div class="tr-cert-in">' +
    '<p class="tr-cert-k">Tumaini Haven Patient Support Programme</p>' +
    '<h2 class="tr-cert-h">Certificate of Completion</h2>' +
    '<p class="tr-cert-l">This certifies that</p>' +
    `<p class="tr-cert-n">${esc(who)}</p>` +
    '<p class="tr-cert-l">has completed every part of the Tumaini 2.0 staff training course, ' +
    'including the check at the end of each part.</p>' +
    '<div class="tr-cert-f">' +
    `<span><b>${summary.total}</b> parts</span>` +
    `<span><b>${esc(fmtLong(summary.seconds))}</b> of training</span>` +
    `<span><b>${esc(day)}</b></span>` +
    '</div>' +
    `<p class="tr-cert-r">Reference ${esc(ref)}</p>` +
    '<p class="tr-cert-s">Practical sign-off with a supervisor is recorded separately.</p>' +
    '</div></section>'
  );
}

/** The banner offering the certificate, once the whole course is done. */
function certBannerHTML(summary) {
  if (!summary.total || summary.done < summary.total) return '';
  return (
    '<section class="tr-cert-cta">' +
    '<p class="tr-cert-cta-k">Course complete</p>' +
    `<h2>All ${summary.total} parts finished</h2>` +
    '<p>Every part watched and every check passed. Download your certificate ' +
    'and keep a copy for your supervisor.</p>' +
    '<button type="button" class="btn pri" data-action="tr-cert">Download certificate</button>' +
    '</section>'
  );
}

function gridHTML(t) {
  const { outline, summary } = outlineOf(t);
  if (!outline.length) {
    return (
      '<div class="today-empty"><h2>No training published yet</h2>' +
      '<p>Course parts appear here the moment they are uploaded.</p></div>'
    );
  }
  const prog = t.progress || {};
  const chapters = outline
    .map(
      (c, i) =>
        '<section class="tr-chapter">' +
        '<header class="tr-ch-head">' +
        `<p class="tr-ch-k">Chapter ${i + 1}</p>` +
        `<h2 class="tr-ch-t">${esc(c.title)}</h2>` +
        `<p class="tr-ch-b">${esc(c.blurb)}</p>` +
        '<div class="tr-ch-p">' +
        meter(c.percent, c.percent === 100 ? 'full' : '') +
        `<span>${c.done} of ${c.total} · ${esc(fmtLong(c.seconds))}</span>` +
        '</div>' +
        '</header>' +
        `<div class="tr-grid">${c.parts.map((v) => partHTML(v, prog)).join('')}</div>` +
        '</section>'
    )
    .join('');
  return certBannerHTML(summary) + resumeHTML(summary) + chapters;
}

// ─── The check ─────────────────────────────────────────────────────────────

function nextPartOf(t, videoId) {
  const { parts } = outlineOf(t);
  const i = parts.findIndex((p) => p.video_id === videoId);
  return i >= 0 && i + 1 < parts.length ? parts[i + 1] : null;
}

function prevPartOf(t, videoId) {
  const { parts } = outlineOf(t);
  const i = parts.findIndex((p) => p.video_id === videoId);
  return i > 0 ? parts[i - 1] : null;
}

function checkHTML(t) {
  if (!_check) return '';
  if (_check.finished) {
    const nx = nextPartOf(t, _open || '');
    return (
      '<div class="tr-check tr-check-done"><h3>Part complete</h3>' +
      '<p>Well done — this part is finished and saved to your record. Anything you missed three times will come back in your certification exam.</p>' +
      '<div class="tr-check-actions">' +
      (nx
        ? `<button type="button" class="btn pri" data-action="tr-open" data-video="${esc(nx.video_id)}">Next part: ${esc(String(nx.number).padStart(2, '0'))} · ${esc(nx.title)}</button>`
        : '<p class="tr-fin">That was the last part of the course. Well done.</p>') +
      '<button type="button" class="btn" data-action="tr-back">All parts</button>' +
      '</div></div>'
    );
  }
  const q = _check.list[_check.idx];
  if (!q) return '';
  const o = _check.lastOutcome;
  const options = (Array.isArray(q.options) ? q.options : [])
    .map((opt, i) => {
      const revealed = o && o.reveal;
      const isRight = i === Number(q.correct_index);
      const cls = revealed && isRight ? ' tr-opt-right' : '';
      const dis = revealed ? ' disabled' : '';
      return (
        `<button type="button" class="tr-opt${cls}" data-action="tr-answer" data-idx="${i}"${dis}>` +
        `<span class="tr-opt-k">${esc(String.fromCharCode(97 + i))}</span><span>${esc(String(opt))}</span></button>`
      );
    })
    .join('');
  const feedback = !o
    ? ''
    : o.reveal
      ? `<div class="tr-fb ${o.correct ? 'ok' : 'warn'}"><b>${o.correct ? 'Correct.' : 'The answer is highlighted above.'}</b> ${esc(o.explanation || '')}</div>` +
        '<div class="tr-check-actions"><button type="button" class="btn pri" data-action="tr-next">' +
        (_check.idx + 1 < _check.list.length ? 'Next question' : 'Finish the check') +
        '</button></div>'
      : `<div class="tr-fb nudge">${esc(o.nudge || '')} <span class="tr-attempts">(attempt ${_check.attempt} of ${MAX_ATTEMPTS})</span></div>`;
  return (
    '<div class="tr-check">' +
    '<div class="tr-check-head">' +
    `<h3>Check — question ${_check.idx + 1} of ${_check.list.length}</h3>` +
    meter(Math.round((_check.idx / _check.list.length) * 100)) +
    '</div>' +
    `<p class="tr-q">${esc(q.prompt)}</p>` +
    `<div class="tr-opts">${options}</div>` +
    feedback +
    '</div>'
  );
}

// ─── The player ────────────────────────────────────────────────────────────

/** The honest note when a part's file will not play. */
function noteHTML() {
  return (
    '<div id="tr-video-note" hidden class="tr-note">' +
    (DEMO_BUILD
      ? '<h3>The demonstration is not part of the demo build</h3>' +
        '<p>This build carries synthetic data only and cannot reach the video store. ' +
        'Signed-in staff see the full recording on the live app — and the check below works here, so you can still practise.</p>'
      : '<h3>This part did not load</h3>' +
        '<p>The video could not be downloaded. Check your connection and try again — ' +
        'if it keeps failing, tell your supervisor which part number it was.</p>') +
    '<div class="tr-check-actions">' +
    (DEMO_BUILD ? '' : '<button type="button" class="btn pri" data-action="tr-reload">Try again</button>') +
    '<button type="button" class="btn" data-action="tr-startcheck">Skip to the check</button>' +
    '</div></div>'
  );
}

function playerHTML(t) {
  const { parts } = outlineOf(t);
  const v = parts.find((x) => x.video_id === _open);
  if (!v) return '';
  const url = videoUrl(v);
  const prev = prevPartOf(t, v.video_id);
  const next = nextPartOf(t, v.video_id);
  const st = partState((t.progress || {})[v.video_id]);
  return (
    '<div class="tr-player">' +
    '<div class="tr-crumb">' +
    '<button type="button" class="btn sm" data-action="tr-back">‹ All parts</button>' +
    `<span class="tr-crumb-ch">${esc(v.chapter)}</span>` +
    `<span class="tr-pill tr-pill-${st.cls}">${esc(st.label)}</span>` +
    '</div>' +
    `<h2 class="tr-play-title"><span class="tr-play-ord">${esc(String(v.number).padStart(2, '0'))}</span>${esc(v.title)}</h2>` +
    '<div class="tr-stage">' +
    (url
      ? `<video id="tr-video" controls${_autoplay ? ' autoplay' : ''} playsinline preload="auto" src="${esc(url)}"></video>`
      : '') +
    noteHTML() +
    '</div>' +
    '<nav class="tr-nav">' +
    (prev
      ? `<button type="button" class="btn" data-action="tr-open" data-video="${esc(prev.video_id)}" title="${esc(prev.title)}">‹ Previous</button>`
      : '<button type="button" class="btn" disabled>‹ Previous</button>') +
    '<button type="button" class="btn" data-action="tr-rewatch">↻ Rewatch</button>' +
    (next
      ? `<button type="button" class="btn" data-action="tr-open" data-video="${esc(next.video_id)}" title="${esc(next.title)}">Next ›</button>`
      : '<button type="button" class="btn" disabled>Next ›</button>') +
    '</nav>' +
    (next ? `<p class="tr-upnext">Up next · ${esc(String(next.number).padStart(2, '0'))} ${esc(next.title)}</p>` : '') +
    // A part watched but not checked used to be a dead end: the check only ever
    // appeared on the 'ended' event, so the ONLY way back to it was sitting
    // through the whole video again. Offer it directly.
    (!_check && st.cls === 'check'
      ? (() => {
          const pr = (t.progress || {})[v.video_id] || {};
          const left = Math.max(0, (pr.questions || 0) - (pr.answered || 0));
          return (
            '<div class="tr-resume-check"><h3>This part is not finished yet</h3>' +
            `<p>You have watched it. <b>${left} of its ${pr.questions || 0} check question` +
            `${(pr.questions || 0) === 1 ? '' : 's'}</b> ${left === 1 ? 'is' : 'are'} still to ` +
            'answer, and the part stays open until they are done. Nothing you have already ' +
            'answered is lost — you pick up where you left off.</p>' +
            '<button type="button" class="btn pri" data-action="tr-takecheck">' +
            (left && left < (pr.questions || 0) ? 'Continue the check' : 'Take the check') +
            '</button></div>'
          );
        })()
      : '') +
    `<div id="tr-check-host">${checkHTML(t)}</div>` +
    '</div>'
  );
}

function render(host) {
  const t = store.get(SLICES.TRAINING) || {};
  let body;
  if (t.loadError) {
    body =
      '<button type="button" class="today-error" data-action="tr-retry">' +
      '<div><h2>Couldn’t load the training — tap to retry</h2>' +
      '<p>The query failed or was denied. Nothing is hidden — tap to try again.</p></div></button>';
  } else if (!t.videos) {
    body = '<p class="view-placeholder">Loading the course…</p>';
  } else {
    body = _open ? playerHTML(t) : gridHTML(t);
  }
  host.innerHTML =
    '<header class="view-head"><h1>Training</h1>' +
    '<p class="view-sub">Your Tumaini 2.0 course — watch each part, then pass its check. Your progress is saved.</p></header>' +
    `<div id="tr-body" class="tr-body">${body}</div>`;
  if (_open) wireVideo();
}

function rerender() {
  const d = doc();
  const host = d && d.getElementById('view');
  const ui = store.get(SLICES.UI) || {};
  if (host && ui.view === 'training') render(host);
}

/**
 * Repaint ONLY the check, leaving the player alone.
 *
 * This is the fix for the bug that stopped parts completing at all. The check
 * used to start with a full re-render, which threw away the <video> element and
 * built a new one carrying `autoplay` — so the instant a nurse finished a part,
 * it started again from zero and the check appeared below the fold. She saw the
 * video restart, assumed nothing had registered, and watched it again. The
 * database showed it exactly: five completes, five check-starts, zero answers.
 *
 * Touching only #tr-check-host keeps the finished video where it is and lets us
 * scroll the question into view instead of hiding it.
 * @param {boolean} [scroll]
 * @returns {void}
 */
function renderCheck(scroll) {
  const d = doc();
  const hostEl = d && d.getElementById('tr-check-host');
  if (!hostEl) {
    rerender();
    return;
  }
  const t = store.get(SLICES.TRAINING) || {};
  hostEl.innerHTML = checkHTML(t);
  if (scroll && typeof hostEl.scrollIntoView === 'function') {
    hostEl.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
  }
}

// ─── Player wiring (direct element events — media events don't delegate) ───

function wireVideo() {
  const d = doc();
  const el = d && d.getElementById('tr-video');
  const note = d && d.getElementById('tr-video-note');
  stopHeartbeat();
  if (!el) {
    // No URL at all (blank storage_path): show the note immediately.
    if (note) note.hidden = false;
    return;
  }
  if (_videoFailed && note) {
    note.hidden = false;
    el.hidden = true;
    return;
  }
  const vid = _open || '';
  const pos = () => Math.round(el.currentTime || 0);
  el.addEventListener('error', () => {
    _videoFailed = true;
    if (note) note.hidden = false;
    el.hidden = true;
  });
  el.addEventListener('play', () => {
    void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'play', position: pos() });
    stopHeartbeat();
    _hb = setInterval(() => {
      // The view may have been replaced under us (navigation) — stop, honestly.
      if (!d.getElementById('tr-video')) {
        stopHeartbeat();
        return;
      }
      if (!el.paused && !el.ended) {
        void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'heartbeat', position: pos() });
      }
    }, 15000);
  });
  el.addEventListener('pause', () => {
    if (!el.ended) void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'pause', position: pos() });
  });
  el.addEventListener('seeking', () => {
    void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'seek', from: _lastPos, to: pos() });
  });
  el.addEventListener('timeupdate', () => {
    _lastPos = pos();
  });
  el.addEventListener('ended', () => {
    stopHeartbeat();
    void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'complete', position: pos() });
    markWatchedLocally(vid);
    startCheck();
  });
}

/** Move the local progress mark so the ✓ appears without waiting for a re-read.
 * @param {string|null} videoId */
function markWatchedLocally(videoId) {
  if (!videoId) return;
  const t = store.get(SLICES.TRAINING) || {};
  const progress = Object.assign({}, t.progress);
  const cur = progress[videoId] || { watched: false, checked: false, done: false, started: false, furthest: 0, percent: 0 };
  progress[videoId] = Object.assign({}, cur, { watched: true, started: true, percent: 100, done: cur.checked });
  store.set(SLICES.TRAINING, { progress });
}

/** @param {string|null} videoId */
function markCheckedLocally(videoId) {
  if (!videoId) return;
  const t = store.get(SLICES.TRAINING) || {};
  const progress = Object.assign({}, t.progress);
  const cur = progress[videoId] || { watched: false, checked: false, done: false, started: false, furthest: 0, percent: 0 };
  progress[videoId] = Object.assign({}, cur, { checked: true, started: true, done: cur.watched });
  store.set(SLICES.TRAINING, { progress });
}

// ─── The check flow ────────────────────────────────────────────────────────

function startCheck() {
  const vid = _open;
  if (!vid) return;
  const t = store.get(SLICES.TRAINING) || {};
  // Only what is still outstanding: an interrupted check must not re-ask
  // questions she has already answered.
  const list = outstandingQuestions(t.questions, vid, t.answers);
  if (!list.length) {
    markCheckedLocally(vid);
    void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'check_complete' });
    _check = { list: [], idx: 0, attempt: 1, finished: true, lastOutcome: null };
    renderCheck(true);
    return;
  }
  _check = { list, idx: 0, attempt: 1, finished: false, lastOutcome: null };
  void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'check_start' });
  renderCheck(true);
}

function handleAnswer(idx) {
  if (!_check || _check.finished) return;
  const q = _check.list[_check.idx];
  if (!q || (_check.lastOutcome && _check.lastOutcome.reveal)) return;
  const outcome = answerOutcome(q, idx, _check.attempt);
  void recordAnswer({
    questionId: q.question_id,
    attemptNo: _check.attempt,
    selectedIndex: idx,
    isCorrect: outcome.correct,
  });
  if (outcome.done) {
    _check.lastOutcome = outcome;
  } else {
    _check.attempt += 1;
    _check.lastOutcome = outcome;
  }
  renderCheck(false);
}

function nextQuestion() {
  if (!_check) return;
  if (_check.idx + 1 < _check.list.length) {
    _check.idx += 1;
    _check.attempt = 1;
    _check.lastOutcome = null;
  } else {
    _check.finished = true;
    markCheckedLocally(_open);
    void logTrainingEvent({ videoId: _open || '', sessionId: _sessionId, type: 'check_complete' });
  }
  renderCheck(false);
}

/** Open a part fresh: new watch session, no carried-over check or error. */
function openPart(vid) {
  if (!vid) return;
  const t0 = store.get(SLICES.TRAINING) || {};
  if (t0.videos && !outlineOf(t0).parts.some((p) => p.video_id === vid)) {
    // Not in this viewer's course (the admin appendix, for a nurse). Do not
    // build a player for it; the presentation gate has to hold on every path
    // into the player, not just the card that normally starts one.
    _open = null;
    rerender();
    return;
  }
  stopHeartbeat();
  _open = vid;
  _sessionId = newSessionId();
  _check = null;
  _lastPos = 0;
  _videoFailed = false;
  _autoplay = true;
  void logTrainingEvent({ videoId: vid, sessionId: _sessionId, type: 'open' });
  rerender();
  const d = doc();
  if (d && d.getElementById('view') && typeof d.getElementById('view').scrollIntoView === 'function') {
    d.getElementById('view').scrollIntoView({ block: 'start' });
  }
}

// ─── Registration ──────────────────────────────────────────────────────────

let _wired = false;

export function registerTraining() {
  if (!_wired) {
    _wired = true;
    register('tr-open', (el) => openPart(el.getAttribute('data-video')));
    register('tr-rewatch', () => openPart(_open));
    register('tr-reload', () => openPart(_open));
    register('tr-back', () => {
      stopHeartbeat();
      _open = null;
      _check = null;
      _videoFailed = false;
      rerender();
    });
    register('tr-takecheck', () => {
      // She has already watched it; go straight to the questions.
      markWatchedLocally(_open);
      startCheck();
    });
    register('tr-startcheck', () => {
      // Demo / missing-file path: count the sitting as watched, then check.
      markWatchedLocally(_open);
      startCheck();
    });
    register('tr-answer', (el) => {
      handleAnswer(Number(el.getAttribute('data-idx')));
    });
    register('tr-next', () => nextQuestion());
    register('tr-cert', () => {
      const d = doc();
      const t = store.get(SLICES.TRAINING) || {};
      const { summary } = outlineOf(t);
      if (!d || summary.done < summary.total) return;
      let host = d.getElementById('tr-cert-host');
      if (!host) {
        host = d.createElement('div');
        host.id = 'tr-cert-host';
        d.body.appendChild(host);
      }
      host.innerHTML = certificateHTML(t, summary);
      d.body.classList.add('printing-cert');
      const done = () => {
        d.body.classList.remove('printing-cert');
        host.innerHTML = '';
      };
      // afterprint does not fire in every browser; clear on a timer as well so
      // the page can never be left stuck in its print-only state.
      if (typeof d.defaultView.onafterprint !== 'undefined') {
        d.defaultView.addEventListener('afterprint', done, { once: true });
      }
      setTimeout(done, 60000);
      d.defaultView.print();
    });
    register('tr-retry', () => {
      void loadTraining().then(rerender);
    });
    store.subscribe(SLICES.TRAINING, () => {
      // Progress marks move as writes land; only repaint the list state.
      const ui = store.get(SLICES.UI) || {};
      if (ui.view === 'training' && !_open) rerender();
    });
  }
  registerView('training', {
    render(host) {
      // A fresh navigation always lands on the course list, never a stale player.
      stopHeartbeat();
      _open = null;
      _check = null;
      _videoFailed = false;
      render(host);
    },
    load() {
      return loadTraining().then((res) => {
        rerender();
        return res;
      });
    },
  });
}
